import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Save, AlertTriangle } from "lucide-react";
import { api, API_BASE, TOKEN_KEY, money } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading } from "@/components/States";
import { InvoiceA4 } from "@/components/InvoiceA4";
import { WorkflowSettings } from "@/components/WorkflowSettings";
import { toast } from "sonner";

// Text fields saved verbatim. Grouped visually but share one save handler.
const BUSINESS_FIELDS = [
  ["name", "Garage Name"], ["trading_name", "Trading Name"], ["company_reg_number", "Company Reg Number"],
  ["vat_number", "VAT Number"], ["phone", "Phone"], ["email", "Email"],
  ["address", "Address"], ["website", "Website"],
];
const BANK_FIELDS = [
  ["bank_name", "Bank Name"], ["bank_account_name", "Account Name"],
  ["sort_code", "Sort Code"], ["account_number", "Account Number"],
];
const INVOICE_FIELDS = [
  ["invoice_prefix", "Invoice Prefix"],
  ["invoice_payment_terms", "Default Payment Terms"],
  ["invoice_footer", "Invoice Footer"],
  ["invoice_notes", "Default Invoice Notes"],
  ["google_review_url", "Google Review URL"],
];
const ALL_TEXT = [...BUSINESS_FIELDS, ...BANK_FIELDS, ...INVOICE_FIELDS];

export default function BusinessSettings() {
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => { setLoading(true); try { setG(await api("/garage/settings")); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const emailRaw = (g.email || "").trim();
    if (emailRaw && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRaw)) {
      toast.error("Enter a valid business email before saving — it's used as the Reply-To on customer emails");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      ALL_TEXT.forEach(([k]) => { const v = g[k]; body[k] = (v === "" || v == null) ? null : v; });
      body.vat_registered = vatReg;
      body.default_hourly_rate = g.default_hourly_rate != null ? Number(g.default_hourly_rate) : null;
      body.default_vat_rate = g.default_vat_rate != null ? Number(g.default_vat_rate) : null;
      body.invoice_next_number = g.invoice_next_number != null ? Number(g.invoice_next_number) : null;
      body.notification_prefs = {
        notify_booking_confirmation: (g.notification_prefs?.notify_booking_confirmation) !== false,
        notify_booking_reschedule: (g.notification_prefs?.notify_booking_reschedule) !== false,
        notify_booking_cancellation: (g.notification_prefs?.notify_booking_cancellation) !== false,
        notify_invoice: (g.notification_prefs?.notify_invoice) !== false,
        notify_quotation: (g.notification_prefs?.notify_quotation) !== false,
        notify_work_order: (g.notification_prefs?.notify_work_order) !== false,
        notify_payment_receipt: (g.notification_prefs?.notify_payment_receipt) !== false,
        notify_payment_overdue: (g.notification_prefs?.notify_payment_overdue) !== false,
      };
      await api("/garage/settings", { method: "PATCH", body: JSON.stringify(body) });
      toast.success("Invoice branding saved — new invoices will use it automatically");
      load();
    } catch (e2) { toast.error(e2.message); } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API_BASE}/garage/logo`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Logo uploaded"); load();
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  };

  if (loading) return <Loading />;

  const field = ([k, label], wide = false) => {
    const invalid = k === "email" && (g[k] || "").trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((g[k] || "").trim());
    return (
      <label key={k} className={`block ${wide ? "sm:col-span-2" : ""}`}>
        <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label.toUpperCase()}</span>
        <input value={g[k] || ""} onChange={(e) => setG({ ...g, [k]: e.target.value })} data-testid={`settings-${k}`}
          className={`w-full rounded-lg border bg-card px-3.5 py-2.5 text-sm outline-none focus:ring-2 ${invalid ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : "border-input focus:border-brand focus:ring-brand/20"}`} />
        {invalid && <span data-testid="settings-email-field-error" className="mt-1 block text-xs font-semibold text-red-600">Enter a valid email address (e.g. name@garage.co.uk).</span>}
      </label>
    );
  };

  // A valid contact email is used as the Reply-To on every customer email.
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((g.email || "").trim());

  // Build a representative preview invoice from the current branding form values.
  const vatReg = g.vat_registered != null ? !!g.vat_registered : !!g.vat_number;
  const rate = Number(g.default_vat_rate) || 20;
  const net = 270, disc = 30, sub = 300;
  const vat = vatReg ? Math.round(net * rate) / 100 : 0;
  const gross = Math.round((net + vat) * 100) / 100;
  const previewBundle = {
    garage: { ...g, vat_registered: vatReg },
    invoice: {
      invoice_number: `${g.invoice_prefix || "INV-"}${g.invoice_next_number || 1001}`,
      created_at: new Date().toISOString(),
      due_at: new Date(Date.now() + 14 * 864e5).toISOString(),
      payment_status: "partial", category_label: "Servicing",
      vat_enabled: vatReg, vat_rate: rate,
      total_subtotal: sub, discount: disc, total_net: net, total_vat: vat, total_gross: gross,
      total_paid: 100, balance: Math.round((gross - 100) * 100) / 100,
      bank_name: g.bank_name, bank_account_name: g.bank_account_name, sort_code: g.sort_code,
      account_number: g.account_number, payment_terms: g.invoice_payment_terms,
      thank_you: "Thank you for business with us!", terms: g.invoice_footer,
      footer: g.invoice_footer, signatory_name: g.name, notes: g.invoice_notes,
    },
    customer: { name: "Sample Customer", address: "12 High Street, London SW1", phone: "020 1234 5678", email: "customer@example.co.uk" },
    vehicle: { vrm: "AB12 CDE", make: "Ford", model: "Focus", year: 2019 },
    line_items: [
      { description: "Full service & oil change", category_label: "Servicing", kind: "labour", qty: 1, unit_price: 180, line_net: 180, line_vat: vatReg ? Math.round(180 * rate) / 100 : 0 },
      { description: "Brake pads (front set)", category_label: "Parts", kind: "part", qty: 2, unit_price: 60, line_net: 120, line_vat: vatReg ? Math.round(120 * rate) / 100 : 0 },
    ],
    job: { ref: "SAMPLE01" },
  };

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Business Settings" subtitle="Invoice branding — set once, applied to every invoice, preview & PDF" />
      {!emailValid && (
        <div data-testid="settings-email-warning" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <p className="font-bold">Add a valid business email</p>
            <p className="mt-0.5">Your Business Settings email is {g.email ? "not a valid address" : "empty"}. It&apos;s used as the <b>Reply-To</b> on every customer email (bookings, quotations, invoices, approvals). Until you set one, customer replies fall back to the Pro Garages support address instead of reaching you directly.</p>
          </div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={save} className="space-y-5 rounded-xl border border-border bg-card p-6">
          {/* Logo */}
          <div className="flex items-center gap-4 rounded-lg bg-secondary p-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-border bg-card">
              {g.logo_url ? <img src={`${API_BASE}/garage/logo/file`} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div>
              <p className="text-sm font-bold text-midnight">Garage Logo</p>
              <p className="text-xs text-muted-foreground">Shown on invoices. JPG/PNG/WEBP, max 5MB.</p>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn" className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-midnight px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </div>
          </div>

          {/* Business details */}
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-xs font-extrabold uppercase tracking-widest text-brand-2">Business Details</legend>
            {BUSINESS_FIELDS.map((f) => field(f, f[0] === "address"))}
            <label className="flex items-center gap-2 sm:col-span-2">
              <input type="checkbox" checked={vatReg} onChange={(e) => setG({ ...g, vat_registered: e.target.checked })} data-testid="settings-vat-registered" className="h-5 w-5 rounded border-input" />
              <span className="text-sm font-bold text-midnight">VAT registered (show VAT on invoices)</span>
            </label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">DEFAULT VAT RATE (%)</span>
              <input type="number" step="0.1" value={g.default_vat_rate ?? ""} onChange={(e) => setG({ ...g, default_vat_rate: e.target.value })} data-testid="settings-vat" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" /></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">HOURLY RATE (£)</span>
              <input type="number" step="0.01" value={g.default_hourly_rate ?? ""} onChange={(e) => setG({ ...g, default_hourly_rate: e.target.value })} data-testid="settings-rate" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" /></label>
          </fieldset>

          {/* Bank details */}
          <fieldset className="grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
            <legend className="mb-1 text-xs font-extrabold uppercase tracking-widest text-brand-2">Bank Details</legend>
            {BANK_FIELDS.map((f) => field(f))}
          </fieldset>

          {/* Invoice defaults */}
          <fieldset className="grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
            <legend className="mb-1 text-xs font-extrabold uppercase tracking-widest text-brand-2">Invoice Defaults</legend>
            {field(["invoice_prefix", "Invoice Prefix"])}
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NEXT INVOICE NUMBER</span>
              <input type="number" value={g.invoice_next_number ?? ""} onChange={(e) => setG({ ...g, invoice_next_number: e.target.value })} data-testid="settings-next-inv" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" /></label>
            {field(["invoice_payment_terms", "Default Payment Terms"], true)}
            {field(["invoice_footer", "Invoice Footer"], true)}
            {field(["invoice_notes", "Default Invoice Notes"], true)}
            {field(["google_review_url", "Google Review URL"], true)}
          </fieldset>

          {/* Booking email notifications */}
          <fieldset className="grid gap-3 border-t border-border pt-4">
            <legend className="mb-1 text-xs font-extrabold uppercase tracking-widest text-brand-2">Customer Email Notifications</legend>
            <p className="text-xs text-muted-foreground">Sent to the customer&apos;s email. Replies go to your garage email ({g.email || "set your contact email above"}).</p>
            {[
              ["notify_booking_confirmation", "Booking confirmations"],
              ["notify_booking_reschedule", "Reschedule notifications"],
              ["notify_booking_cancellation", "Cancellation notifications"],
              ["notify_invoice", "Invoice emails"],
              ["notify_quotation", "Quotation emails"],
              ["notify_work_order", "Work order emails"],
              ["notify_payment_receipt", "Payment receipts"],
              ["notify_payment_overdue", "Overdue payment reminders"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={(g.notification_prefs?.[k]) !== false}
                  onChange={(e) => setG({ ...g, notification_prefs: { ...(g.notification_prefs || {}), [k]: e.target.checked } })}
                  data-testid={`settings-${k.replace(/_/g, "-")}`}
                  className="h-5 w-5 rounded border-input"
                />
                {label}
              </label>
            ))}
          </fieldset>

          <button type="submit" disabled={saving} data-testid="settings-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" />SAVE BRANDING</>}
          </button>
        </form>

        {/* Live A4 preview */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-muted-foreground">Live A4 Preview</p>
          <div className="max-h-[80vh] overflow-auto rounded-xl border border-border bg-secondary p-3" data-testid="settings-invoice-preview">
            <div className="origin-top scale-[0.92]"><InvoiceA4 bundle={previewBundle} /></div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Sample data shown. {vatReg ? `VAT at ${rate}% is displayed.` : "VAT is hidden (not VAT registered)."} Subtotal {money(sub)} · discount -{money(disc)}.</p>
        </div>
      </div>

      <WorkflowSettings />
    </div>
  );
}
